/**
 * 素材卡片业务埋点组件
 *
 * 封装素材卡片相关的埋点逻辑，包括：
 * - 素材点击事件（material_click）
 * - 素材曝光事件（content_expose）
 * - 自动处理数据格式转换（ID 转字符串）
 * - 自动处理 content_type、ext、material_type 等数据
 * - 自动处理 module_name 和 search_word（条件传入）
 */

import { getContentType } from "@/utils/getContentType";
import React, { useMemo } from "react";
import { Track } from "../components/Track";

export interface MaterialCardProps {
  /**
   * Pin 数据
   */
  pin?: {
    pin_id?: string | number;
    board_id?: string | number;
    file_id?: string | number;
    file_material?: {
      material_id?: string | number;
      extra?: {
        group_ids?: any;
      };
    };
    extra?: any;
    ext?: Record<string, any>;
  };

  /**
   * 模块名称（可选）
   */
  moduleName?: string;

  /**
   * 搜索词（可选，通常由 SearchContext 自动注入）
   */
  searchWord?: string;

  /**
   * 子元素（卡片组件）
   */
  children: React.ReactNode;

  /**
   * 是否启用曝光埋点
   * @default true
   */
  enableExpose?: boolean;

  /**
   * 曝光阈值（0-1），默认 0.6（60%）
   */
  exposeThreshold?: number;

  /**
   * 曝光时长（ms），默认 1500ms
   */
  exposeTime?: number;

  /**
   * 曝光索引（可选）
   */
  exposeIndex?: number;

  /**
   * 素材类型（可选，如"套系素材"）
   */
  materialType?: string;

  /**
   * 前置连续跳转内容 file_id 列表（可选）
   */
  sourceFileIds?: string[];
}

/**
 * 素材卡片业务埋点组件
 *
 * @example
 * ```tsx
 * <MaterialCard pin={pin} moduleName={moduleName} searchWord={searchWord}>
 *   <div className={styles.card}>
 *     <img src={pin.file.url} />
 *   </div>
 * </MaterialCard>
 * ```
 */
export function MaterialCard({
  pin,
  moduleName,
  searchWord,
  children,
  enableExpose = true,
  exposeThreshold,
  exposeTime,
  exposeIndex,
  materialType,
  sourceFileIds,
}: MaterialCardProps) {
  // 构建点击埋点数据（使用 useMemo 缓存，避免每次渲染都创建新对象）
  const clickTrackData = useMemo(() => {
    const data: Record<string, any> = {};

    // 处理 ID 字段（确保是字符串类型）
    if (pin?.pin_id !== undefined) {
      data.pin_id = String(pin.pin_id);
    }
    if (pin?.board_id !== undefined) {
      data.board_id = String(pin.board_id);
    }
    if (pin?.file_id !== undefined) {
      data.file_id = String(pin.file_id);
    }

    // 处理 content_type
    if (pin) {
      data.content_type = getContentType(pin as any);
    }

    // 处理 content_id（material_id）
    if (pin?.file_material?.material_id !== undefined) {
      data.content_id = String(pin.file_material.material_id);
    }

    // 处理 ext 数据（material_click 中 ext 会 Object.assign 到 trackData）
    if (pin?.ext) {
      Object.assign(data, pin.ext);
    }

    // 处理 material_type
    if (materialType) {
      data.material_type = materialType;
    } else if (pin?.file_material?.extra?.group_ids) {
      // 自动判断是否为套系素材
      data.material_type = "套系素材";
    }

    // 处理 source_file_ids
    if (sourceFileIds && sourceFileIds.length > 0) {
      data.source_file_ids = sourceFileIds;
    }

    // 条件传入 module_name 和 search_word（避免 undefined）
    if (moduleName) {
      data.module_name = moduleName;
    }
    if (searchWord) {
      data.search_word = searchWord;
    }

    return data;
  }, [pin, moduleName, searchWord, materialType, sourceFileIds]);

  // 构建曝光埋点数据（使用 useMemo 缓存，避免每次渲染都创建新对象）
  const exposeTrackData = useMemo(() => {
    const data: Record<string, any> = {};

    // 处理 ID 字段
    if (pin?.pin_id !== undefined) {
      data.pin_id = String(pin.pin_id);
    }
    if (pin?.board_id !== undefined) {
      data.board_id = String(pin.board_id);
    }

    // 处理 content_type
    if (pin) {
      data.content_type = getContentType(pin as any);
    }

    // 处理 ext 数据（content_expose 中 ext 是单独的对象）
    if (pin?.ext) {
      data.ext = pin.ext;
    }

    // 处理 file_id（content_expose 中 file_id 会放到 ext.file_id）
    if (pin?.file_id !== undefined) {
      data.ext = data.ext || {};
      data.ext.file_id = String(pin.file_id);
    }

    // 处理 expose_index
    if (exposeIndex !== undefined) {
      data.expose_index = exposeIndex;
    }

    // 处理 source_file_ids
    if (sourceFileIds && sourceFileIds.length > 0) {
      data.source_file_ids = sourceFileIds;
    }

    // 条件传入 module_name 和 search_word
    if (moduleName) {
      data.module_name = moduleName;
    }
    if (searchWord) {
      data.search_word = searchWord;
    }

    return data;
  }, [pin, moduleName, searchWord, exposeIndex, sourceFileIds]);

  return (
    <Track
      event="material_click"
      data={clickTrackData}
      exposeEvent={enableExpose ? "content_expose" : undefined}
      exposeData={enableExpose ? exposeTrackData : undefined}
      exposeThreshold={exposeThreshold}
      exposeTime={exposeTime}
    >
      {children}
    </Track>
  );
}
